import { main } from './main'

// The default language (english) must have a value for every message.
// Other languages may have missing messages. If the application finds
// a missing message for the current language, it will use the english
// translation instead.

/** @public */
export const TRANSLATIONS: TLListedTranslation[] = [
	{ locale: 'ar', label: 'عربي' },
	{ locale: 'da', label: 'Danish' },
	{ locale: 'de', label: 'Deutsch' },
	{ locale: 'en', label: 'English' },
	{ locale: 'es', label: 'Español' },
	{ locale: 'fa', label: 'فارسی' },
	{ locale: 'fr', label: 'Français' },
	{ locale: 'he', label: 'עברית' },
	{ locale: 'it', label: 'Italiano' },
	{ locale: 'ja', label: '日本語' },
	{ locale: 'ko-kr', label: '한국어' },
	{ locale: 'ne', label: 'नेपाली' },
	{ locale: 'no', label: 'Norwegian' },
	{ locale: 'pl', label: 'Polski' },
	{ locale: 'pt-br', label: 'Português - Brasil' },
	{ locale: 'ru', label: 'Russian' },
	{ locale: 'sv', label: 'Svenska' },
	{ locale: 'tr', label: 'Türkçe' },
	{ locale: 'uk', label: 'Ukrainian' },
	{ locale: 'zh-cn', label: 'Chinese - Simplified' },
	{ locale: 'zh-tw', label: '繁體中文 (台灣)' },
]

/* ----------------- (do not change) ---------------- */

/** @public */
export type TLListedTranslation = {
	readonly locale: string
	readonly label: string
}

/** @public */
export type TLTranslationMessages = typeof main

/** @public */
export type TLTranslationKey = keyof TLTranslationMessages

/** @public */
export type TLTranslation = {
	readonly locale: string
	readonly label: string
	readonly messages: TLTranslationMessages
}

/** @public */
export type TLTranslations = TLTranslation[]

/** @public */
export type TLTranslationLocale = TLTranslations[number]['locale']

TRANSLATIONS.sort((a, b) => (a.locale < b.locale ? -1 : 1))

/** @public */
export const DEFAULT_TRANSLATION: TLTranslation = {
	locale: 'en',
	label: 'English',
	messages: main,
}

/** @public */
export async function fetchTranslation(locale: TLTranslationLocale): Promise<TLTranslation> {
	const translation = TRANSLATIONS.find((t) => t.locale === locale)

	if (!translation) {
		console.warn(`No translation found for locale ${locale}`)
		return DEFAULT_TRANSLATION
	}

	const res = await fetch(`/translations/${locale}.json`)
	const messages: TLTranslationMessages = await res.json()

	if (!messages) {
		console.warn(`No messages found for locale ${locale}`)
		return DEFAULT_TRANSLATION
	}

	const missing: string[] = []

	for (const key in main) {
		if (!messages[key as keyof typeof main]) {
			missing.push(key)
		}
	}

	if (missing.length > 0) {
		console.warn(`Language ${locale}: missing messages for keys:\n${missing.join('\n')}`)
	}

	return { ...translation, messages: { ...main, ...messages } }
}

/** @public */
export async function getTranslation(locale: TLTranslationLocale): Promise<TLTranslation> {
	return locale === 'en' ? DEFAULT_TRANSLATION : await fetchTranslation(locale)
}
